import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';

import { mergeSx } from '@/utils/mergeSx';

import {
	createMeasureContext,
	fitMiddle,
	getFont,
	getLetterSpacing,
	measureWidth,
	whenFontsReady
} from './textMeasurement';
import { truncatedTextStyles as sx } from './TruncatedText.styles';

export type TruncatedTextVariant = 'end' | 'middle';

type Props = {
	text: string;
	variant?: TruncatedTextVariant;
	endAdornment?: ReactNode;
	// Full untruncated width of the actual text node, not an ancestor, so
	// it's correct regardless of font styling in between. Only fires for
	// 'middle'.
	onNaturalWidthChange?: (width: number | null) => void;
	customSx?: SxProps<Theme>;
	onClick?: () => void;
	children?: ReactNode;
};

// variant='middle' consumers must let this component grow to the row's full
// width (flex: '1 1 auto', not fit-content) and shrink below the text's
// natural width (min-width: 0 on every ancestor) — it measures its own
// rendered box, not the text, to decide truncation. Structural wrappers in
// that chain need display: 'contents', not 'block', or they'll hug content
// and break the measurement (see ParentHierarchyBreadcrumb/ParentHierarchyTree).
const MiddleTruncatedText = ({
	text,
	endAdornment,
	onNaturalWidthChange,
	customSx,
	onClick
}: Omit<Props, 'variant'>) => {
	const rootRef = useRef<HTMLSpanElement>(null);
	const fontRef = useRef<HTMLSpanElement>(null);
	const adornmentRef = useRef<HTMLSpanElement>(null);
	const [display, setDisplay] = useState(text);

	useLayoutEffect(() => {
		const root = rootRef.current;
		const fontNode = fontRef.current;
		if (!root || !fontNode) return;

		const ctx = createMeasureContext();
		if (!ctx) return;

		const recompute = () => {
			ctx.font = getFont(fontNode);
			const letterSpacing = getLetterSpacing(fontNode);

			onNaturalWidthChange?.(measureWidth(ctx, text, letterSpacing));

			const rootStyle = window.getComputedStyle(root);
			const gap =
				parseFloat(rootStyle.columnGap) || parseFloat(rootStyle.gap) || 0;
			const adornmentWidth = adornmentRef.current
				? adornmentRef.current.getBoundingClientRect().width + gap
				: 0;

			const maxWidth = root.getBoundingClientRect().width - adornmentWidth;
			if (maxWidth <= 0) {
				setDisplay(text);
				return;
			}

			setDisplay(fitMiddle(ctx, text, maxWidth, letterSpacing));
		};

		recompute();
		whenFontsReady(recompute);

		const resizeObserver = new ResizeObserver(recompute);
		resizeObserver.observe(root);
		if (adornmentRef.current) resizeObserver.observe(adornmentRef.current);

		return () => resizeObserver.disconnect();
	}, [text, onNaturalWidthChange]);

	// fitMiddle already tells us if it truncated: the fitted string differs
	// from the original.
	const isTruncated = display !== text;

	return (
		<Box
			component='span'
			ref={rootRef}
			onClick={onClick}
			sx={mergeSx(
				sx.middleRoot(!!endAdornment),
				customSx,
				!!onClick && { cursor: 'pointer' }
			)}
		>
			{/* Always wraps the text, rather than conditionally rendering, so
			    this stays the same element across truncation-state changes —
			    swapping would remount it and orphan the ResizeObserver. */}
			<Tooltip
				title={text}
				disableHoverListener={!isTruncated}
				disableFocusListener={!isTruncated}
				disableTouchListener={!isTruncated}
			>
				<Box
					component='span'
					ref={fontRef}
					data-truncated-text=''
					sx={sx.middleText}
				>
					{display}
				</Box>
			</Tooltip>

			{endAdornment && (
				<Box component='span' ref={adornmentRef} sx={sx.adornment}>
					{endAdornment}
				</Box>
			)}
		</Box>
	);
};

const EndTruncatedText = ({
	text,
	customSx,
	onClick,
	children
}: Omit<Props, 'variant' | 'endAdornment'>) => {
	const ref = useRef<HTMLSpanElement>(null);
	const [isTruncated, setIsTruncated] = useState(false);

	useLayoutEffect(() => {
		const node = ref.current;
		if (!node) return;

		const checkOverflow = () => {
			setIsTruncated(node.scrollWidth > node.clientWidth);
		};

		checkOverflow();

		const resizeObserver = new ResizeObserver(checkOverflow);
		resizeObserver.observe(node);

		return () => resizeObserver.disconnect();
	}, [text]);

	return (
		<Tooltip
			title={text}
			disableHoverListener={!isTruncated}
			disableFocusListener={!isTruncated}
			disableTouchListener={!isTruncated}
		>
			<Box
				component='span'
				ref={ref}
				onClick={onClick}
				sx={mergeSx(
					sx.endEllipsis,
					customSx,
					!!onClick && { cursor: 'pointer' }
				)}
			>
				{children ?? text}
			</Box>
		</Tooltip>
	);
};

export const TruncatedText = ({
	text,
	variant = 'end',
	endAdornment,
	onNaturalWidthChange,
	customSx,
	onClick,
	children
}: Props) => {
	if (variant === 'middle') {
		return (
			<MiddleTruncatedText
				text={text}
				endAdornment={endAdornment}
				onNaturalWidthChange={onNaturalWidthChange}
				customSx={customSx}
				onClick={onClick}
			/>
		);
	}

	return (
		<EndTruncatedText text={text} customSx={customSx} onClick={onClick}>
			{children}
		</EndTruncatedText>
	);
};
